import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, Repeat, Shuffle } from 'lucide-react';
import { useAudio } from '~/contexts/AudioContext';

interface Track {
  title: string;
  file: string;
}

const soundtrack: Track[] = [
  { title: 'Lumora: The First Dawn', file: '/music/Lumora- The First Dawn.mp3' },
  { title: 'Peaceful Voyage – Daylight at Sea', file: '/music/01-01-Peaceful Voyage – Daylight at Sea.mp3' },
  { title: 'Peaceful Voyage – Daylight at Sea (Battle)', file: '/music/01-02-Peaceful Voyage – Daylight at Sea (battle).mp3' },
  { title: 'Portsmuth in Day', file: '/music/Portsmuth in Day.mp3' },
  { title: 'Portsmuth at Night', file: '/music/02-00-Portsmuth at Night.mp3' },
  { title: 'A Fateful Encounter', file: '/music/02_A Fateful Encounter_trimmed.mp3' },
  { title: 'Moonlight Pursuit', file: '/music/02-02-Moonlight Pursuit.mp3' },
  { title: 'Chains Beneath the Crown', file: '/music/Chains Beneath the Crown.mp3' },
  { title: 'Blades of Resolve', file: '/music/Blades of Resolve (Main Battle Theme).mp3' },
  { title: 'Echoes of the Ascetic (Day)', file: '/music/Echoes of the Ascetic (Democratic Faction Theme - Day).mp3' },
  { title: 'Echoes of the Ascetic – Night Vigil', file: '/music/Echoes of the Ascetic – Night Vigil.mp3' },
  { title: 'Throne of Iron (Day)', file: '/music/Throne of Iron (Theme for Kratocratic Faction - Day).mp3' },
  { title: 'Throne of Iron (Night)', file: '/music/Throne of Iron (Night).mp3' },
  { title: 'Crown and Scepter', file: '/music/Crown and Scepter (Monarchy Theme).mp3' },
  { title: 'Crown and Scepter – Night Repose', file: '/music/Crown and Scepter – Night Repose.mp3' },
  { title: 'Lumora Overworld Theme', file: '/music/Lumora Overworld Theme.mp3' },
  { title: 'Lumora Overworld Theme - Night', file: '/music/Lumora Overworld Theme - Night.mp3' },
  { title: "Gaia's Paradise", file: "/music/Gaia's Paradise (Neutral Faction Theme Daytime).mp3" },
  { title: "Gaia's Paradise (Night)", file: "/music/Gaia's Paradise (Night).mp3" },
];

export function MusicPlayer() {
  // Get volume state from global AudioContext
  const { volume, setVolume, isMuted, toggleMute: contextToggleMute } = useAudio();

  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRepeat, setIsRepeat] = useState(false);
  const [isShuffle, setIsShuffle] = useState(false);
  const [shuffleHistory, setShuffleHistory] = useState<number[]>([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [trackDurations, setTrackDurations] = useState<{ [key: number]: number }>({});
  const [isHydrated, setIsHydrated] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Track hydration state to prevent mismatch
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const currentTrack = soundtrack[currentTrackIndex];

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      if (isRepeat) {
        // Repeat current track
        audio.currentTime = 0;
        audio.play();
      } else {
        // Auto-play next track
        handleNext();
      }
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentTrackIndex, isRepeat, isShuffle, shuffleHistory]);

  // Load metadata when track changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Force load metadata for the new track
    audio.load();

    // Set duration immediately if already loaded
    if (audio.duration) {
      setDuration(audio.duration);
    }
  }, [currentTrackIndex]);

  // Sync context volume to local audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.muted = isMuted;
    }
  }, [volume, isMuted]);

  // Load durations for all tracks
  useEffect(() => {
    const loadDurations = async () => {
      const durations: { [key: number]: number } = {};

      for (let i = 0; i < soundtrack.length; i++) {
        const audio = new Audio(soundtrack[i].file);
        await new Promise<void>((resolve) => {
          audio.addEventListener('loadedmetadata', () => {
            durations[i] = audio.duration;
            resolve();
          });
          audio.addEventListener('error', () => {
            durations[i] = 0;
            resolve();
          });
        });
      }

      setTrackDurations(durations);
    };

    loadDurations();
  }, []);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleNext = () => {
    if (isShuffle) {
      // Shuffle mode: select random track that hasn't been played yet
      const availableTracks = Array.from({ length: soundtrack.length }, (_, i) => i)
        .filter(i => !shuffleHistory.includes(i));

      if (availableTracks.length === 0) {
        // All tracks have been played, reset history and start new cycle
        setShuffleHistory([]);
        // Select random track from all tracks
        const randomIndex = Math.floor(Math.random() * soundtrack.length);
        setCurrentTrackIndex(randomIndex);
        setShuffleHistory([randomIndex]);
      } else {
        // Select random track from available tracks
        const randomIndex = availableTracks[Math.floor(Math.random() * availableTracks.length)];
        setCurrentTrackIndex(randomIndex);
        setShuffleHistory(prev => [...prev, randomIndex]);
      }
    } else {
      // Sequential mode: next track in order
      setCurrentTrackIndex((prev) => (prev + 1) % soundtrack.length);
    }

    setIsPlaying(true);
    setTimeout(() => {
      audioRef.current?.play();
    }, 100);
  };

  const handlePrevious = () => {
    setCurrentTrackIndex((prev) => (prev - 1 + soundtrack.length) % soundtrack.length);
    setIsPlaying(true);
    setTimeout(() => {
      audioRef.current?.play();
    }, 100);
  };

  const toggleMute = () => {
    // Use context's toggleMute to sync across all players
    contextToggleMute();
  };

  const toggleRepeat = () => {
    setIsRepeat(!isRepeat);
  };

  const toggleShuffle = () => {
    const newShuffleState = !isShuffle;
    setIsShuffle(newShuffleState);

    if (newShuffleState) {
      // Turning shuffle ON
      if (shuffleHistory.length === 0) {
        // No tracks played yet in shuffle mode, start with random track
        const randomIndex = Math.floor(Math.random() * soundtrack.length);
        setCurrentTrackIndex(randomIndex);
        setShuffleHistory([randomIndex]);
      }
    } else {
      // Turning shuffle OFF - reset shuffle history
      setShuffleHistory([]);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    // Use context's setVolume to sync across all players
    setVolume(newVolume);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-purple-950/20 to-black" />

      <div className="container relative mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Lumora Soundtrack
            </h2>
            <p className="text-gray-400 text-lg">
              Immerse yourself in the music of Lumora
            </p>
          </div>

          {/* Music Player Card */}
          <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-8 shadow-2xl">
            <audio ref={audioRef} src={currentTrack.file} />

            {/* Current Track Info */}
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-white mb-2">
                {currentTrack.title}
              </h3>
              <p className="text-purple-300 text-sm">
                Track {currentTrackIndex + 1} of {soundtrack.length}
              </p>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <input
                type="range"
                min="0"
                max={duration || 0}
                value={currentTime}
                onChange={handleSeek}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, #a855f7 0%, #a855f7 ${(currentTime / duration) * 100}%, #374151 ${(currentTime / duration) * 100}%, #374151 100%)`,
                }}
              />
              <div className="flex justify-between text-sm text-gray-400 mt-2">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-6 mb-6">
              <button
                onClick={toggleRepeat}
                className={`transition-all p-2 rounded-lg ${
                  isRepeat
                    ? 'bg-purple-600/50 text-purple-300'
                    : 'text-white hover:text-purple-400 hover:bg-purple-900/20'
                }`}
                aria-label={isRepeat ? 'Disable repeat' : 'Enable repeat'}
              >
                <Repeat size={28} />
              </button>

              <button
                onClick={handlePrevious}
                className="text-white hover:text-purple-400 transition-colors p-2"
                aria-label="Previous track"
              >
                <SkipBack size={28} />
              </button>

              <button
                onClick={togglePlay}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-full p-4 transition-colors shadow-lg hover:shadow-purple-500/50"
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? <Pause size={32} /> : <Play size={32} className="ml-1" />}
              </button>

              <button
                onClick={handleNext}
                className="text-white hover:text-purple-400 transition-colors p-2"
                aria-label="Next track"
              >
                <SkipForward size={28} />
              </button>

              <button
                onClick={toggleShuffle}
                className={`transition-all p-2 rounded-lg ${
                  isShuffle
                    ? 'bg-purple-600/50 text-purple-300'
                    : 'text-white hover:text-purple-400 hover:bg-purple-900/20'
                }`}
                aria-label={isShuffle ? 'Disable shuffle' : 'Enable shuffle'}
              >
                <Shuffle size={28} />
              </button>
            </div>

            {/* Volume Control */}
            <div className="flex items-center justify-center gap-4 max-w-xs mx-auto">
              <button
                onClick={toggleMute}
                className="text-white hover:text-purple-400 transition-colors"
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted || volume === 0 ? <VolumeX size={24} /> : <Volume2 size={24} />}
              </button>
              {isHydrated ? (
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #a855f7 0%, #a855f7 ${(isMuted ? 0 : volume) * 100}%, #374151 ${(isMuted ? 0 : volume) * 100}%, #374151 100%)`,
                  }}
                  aria-label="Volume"
                />
              ) : (
                <div className="flex-1 h-2 bg-gray-700 rounded-lg" />
              )}
            </div>

            {/* Track List */}
            <div className="mt-8 pt-8 border-t border-purple-500/30">
              <h4 className="text-lg font-semibold text-white mb-4">Playlist</h4>
              <div className="grid gap-2 max-h-64 overflow-y-auto custom-scrollbar">
                {soundtrack.map((track, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setCurrentTrackIndex(index);
                      setIsPlaying(true);
                      // Reset shuffle history when manually selecting a track
                      setShuffleHistory([index]);
                      setTimeout(() => {
                        audioRef.current?.play();
                      }, 100);
                    }}
                    className={`text-left px-4 py-3 rounded-lg transition-colors ${
                      index === currentTrackIndex
                        ? 'bg-purple-600/40 text-white'
                        : 'text-gray-300 hover:bg-purple-900/20 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-400 w-6">
                        {index + 1}
                      </span>
                      <span className="flex-1">{track.title}</span>
                      <span className="text-gray-400 text-sm">
                        {trackDurations[index] ? formatTime(trackDurations[index]) : '...'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          background: #a855f7;
          cursor: pointer;
          border-radius: 50%;
          box-shadow: 0 0 8px rgba(168, 85, 247, 0.5);
        }

        .slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          background: #a855f7;
          cursor: pointer;
          border-radius: 50%;
          border: none;
          box-shadow: 0 0 8px rgba(168, 85, 247, 0.5);
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(55, 65, 81, 0.5);
          border-radius: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #a855f7;
          border-radius: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9333ea;
        }
      `}</style>
    </section>
  );
}
